import type { Block, Inline } from '@contentful/rich-text-types';
import { MARKS } from '@contentful/rich-text-types';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import * as S from './CardRichTextRenderer.styles';

export type Mark = {
  type: string;
};

export type NodeData = {
  [key: string]: unknown;
};

export type NodeContent = {
  content?: NodeContent[];
  data?: NodeData;
  marks?: Mark[];
  nodeType: string;
  value?: string;
};

export type DocumentNode = {
  content: NodeContent[];
  data: NodeData;
  nodeType: string;
};

export type Content = DocumentNode[];

export const renderTableRow = (
  node: Block | Inline,
  children: ReactNode,
  data: string[],
) => {
  return <S.StyledRow data={data}>{children}</S.StyledRow>;
};

export const RENDER_TEXT_OPTION = (text: string) => {
  return text
    .split('\n')
    .reduce<ReactNode[]>((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment];
    }, []);
};
export const renderMarkOption = {
  [MARKS.BOLD]: (text: ReactNode) => <strong>{text}</strong>,
  [MARKS.ITALIC]: (text: ReactNode) => <S.StyledEm>{text}</S.StyledEm>,
  [MARKS.UNDERLINE]: (text: ReactNode) => <u>{text}</u>,
  [MARKS.CODE]: (text: ReactNode) => <S.StyledCode>{text}</S.StyledCode>,
  [MARKS.SUBSCRIPT]: (text: ReactNode) => (
    <S.StyledSubscript>{text}</S.StyledSubscript>
  ),
  [MARKS.SUPERSCRIPT]: (text: ReactNode) => (
    <S.StyledSuperscript>{text}</S.StyledSuperscript>
  ),
};

export const useTableData = (content: Content | undefined) => {
  const [data, setData] = useState<string[]>([]);

  useEffect(() => {
    const findTableData = (content: Content) => {
      const tableNode = content.find((item: DocumentNode) => {
        return item?.nodeType === 'table';
      });
      if (tableNode?.content[0]?.content) {
        return tableNode.content[0].content
          .filter(
            (cell: NodeContent | undefined): cell is NodeContent => !!cell,
          )
          .map((cell: NodeContent) => {
            return cell.content?.[0]?.content?.[0]?.value;
          });
      }
      return [];
    };

    if (content) {
      const tableData = findTableData(content);
      const filteredTableData = tableData.filter(
        (item) => item !== undefined,
      ) as string[];
      setData(filteredTableData);
    }
  }, [content]);

  return data;
};
